import { memo, useMemo } from "react";
import { type ColumnDef, type ColumnFiltersState, type SortingState } from "@tanstack/react-table";
import { DataTable } from "@/ui/molecules";
import { StatusBadge } from "@/ui/molecules";
import { cn, formatDate } from "@/utils/utils";
import { formatDateTimeEuropeBerlin } from "@/utils/europeBerlinTime";
import type { TFinancialFile, TFileArchiveReason, TFileStatus, TFileType } from "@/store";

export interface TFilesTableProps {
    files: TFinancialFile[];
    loading?: boolean;
    onFileClick?: (file: TFinancialFile) => void;
    className?: string;
    /** Controlled column filters (server mode). */
    columnFilters?: ColumnFiltersState;
    initialColumnFilters?: ColumnFiltersState;
    onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
    sortingState?: SortingState;
    onSortingChange?: (sorting: SortingState) => void;
    rowCount?: { filtered: number; total: number; isFiltered: boolean };
    /** Dynamic facet options from /files/filter-options. */
    businessPartnerOptions?: { value: string; label: string }[];
    submissionOptions?: { value: string; label: string }[];
    enableFilters?: boolean;
    hiddenColumns?: string[];
    availableFileTypes?: ReadonlySet<TFileType>;
    /**
     * Append the archive-only columns (Reason, Archived). Additive twin of
     * `hiddenColumns`: only the Archive page sets it, so the other three file
     * listings — where every row is live and both fields are null — stay unchanged.
     */
    showArchiveColumns?: boolean;
    storageKey?: string;
    /** Override the message shown when the table body is empty. Defaults to the "upload a file" prompt. */
    emptyMessage?: string;
    /** Whether to show the row-count bar. Forwarded to DataTable. Defaults to true. */
    showRowCount?: boolean;
}

const fileStatusOptions: { value: TFileStatus; label: string }[] = [
    { value: "not_received", label: "Not Received" },
    { value: "uploaded", label: "Uploaded" },
    { value: "under_acceptance", label: "Under Acceptance" },
    { value: "under_review", label: "Under Review" },
    { value: "ready_for_balancing", label: "Ready for Balancing" },
    { value: "balanced", label: "Balanced" },
    { value: "ready_for_commitment", label: "Ready for Commitment" },
    { value: "committed", label: "Committed" },
    { value: "adjusted", label: "Adjusted" },
    { value: "finjournal_generated", label: "FinJournal Generated" },
    { value: "data_platform_generated", label: "Data Platform Generated" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
];

const ALL_FILE_TYPE_OPTIONS: { value: TFileType; label: string }[] = [
    { value: "tb", label: "Trial Balance" },
    { value: "gl", label: "General Ledger" },
    { value: "otb", label: "Opening TB" },
    { value: "fin_journals", label: "Financial Journals" },
    { value: "tb_gl", label: "Trial Balance & General Ledger" },
];

type TFilterOptions = {
    businessPartnerOptions: { value: string; label: string }[];
    submissionOptions: { value: string; label: string }[];
    fileTypeOptions: { value: TFileType; label: string }[];
};

const FILE_ARCHIVE_REASON_LABELS: Record<TFileArchiveReason, string> = {
    retention_period: "Retention period (3 months)",
    deal_inactive: "Deal deactivated",
};

/** Placeholder cell for a value that only archived rows carry. */
const emptyCell = () => <span className="text-muted-foreground">—</span>;

const archiveColumns: ColumnDef<TFinancialFile>[] = [
    {
        id: "archiveReason",
        accessorKey: "archiveReason",
        header: "Reason",
        cell: ({ row }) => {
            const reason = row.original.archiveReason;
            return reason ? FILE_ARCHIVE_REASON_LABELS[reason] : emptyCell();
        },
    },
    {
        id: "archivedAt",
        accessorKey: "archivedAt",
        header: "Archived",
        cell: ({ row }) =>
            row.original.archivedAt
                ? formatDateTimeEuropeBerlin(row.original.archivedAt)
                : emptyCell(),
    },
    {
        id: "dealIsActive",
        accessorKey: "dealIsActive",
        header: "Deal Active",
        // Not sortable: the value lives on the joined deal, not on the file, so the
        // server has no sort key for it.
        enableSorting: false,
        cell: ({ row }) => {
            const active = row.original.dealIsActive;
            // null means no deal at all (standalone FinJournal) — distinct from an
            // inactive deal, so it renders as neither Yes nor No.
            if (active === null) return emptyCell();
            return (
                <span
                    className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                        active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600",
                    )}
                >
                    {active ? "Yes" : "No"}
                </span>
            );
        },
    },
];

const createColumns = ({
    businessPartnerOptions,
    submissionOptions,
    fileTypeOptions,
}: TFilterOptions): ColumnDef<TFinancialFile>[] => [
    {
        // Server filters by service_provider_ids; the column id maps to that param.
        id: "serviceProviderName",
        accessorKey: "serviceProviderName",
        header: "Business Partner Name",
        cell: ({ row }) =>
            row.original.fileType === "fin_journals" ? (
                <span className="text-muted-foreground">—</span>
            ) : (
                <div className="max-w-xs truncate" title={row.original.serviceProviderName}>
                    {row.original.serviceProviderName}
                </div>
            ),
        meta: {
            filterType: "multiSelect",
            filterOptions: businessPartnerOptions,
            filterSearchable: true,
        },
    },
    {
        id: "dealName",
        accessorKey: "dealName",
        header: "Submission Name",
        cell: ({ row }) =>
            row.original.fileType === "fin_journals" ? (
                <span className="text-muted-foreground">—</span>
            ) : (
                row.original.dealName
            ),
        meta: {
            filterType: "multiSelect",
            filterOptions: submissionOptions,
            filterSearchable: true,
        },
    },
    {
        accessorKey: "fileType",
        header: "Type",
        cell: ({ row }) => (
            <span className="uppercase text-xs font-medium">{row.original.fileType}</span>
        ),
        meta: {
            filterType: "select",
            filterOptions: fileTypeOptions,
            filterPlaceholder: "All types",
        },
    },
    {
        accessorKey: "period",
        header: "Period",
        cell: ({ row }) => formatDate(row.original.period),
        meta: { filterType: "date" },
    },
    {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
        meta: {
            filterType: "select",
            filterOptions: fileStatusOptions,
            filterPlaceholder: "All statuses",
        },
    },
    {
        accessorKey: "lineItemCount",
        header: "Line Items",
        cell: ({ row }) => (row.original.lineItemCount ?? 0).toLocaleString(),
        meta: { filterType: "numberRange" },
    },
    {
        accessorKey: "uploadedAt",
        header: "Uploaded",
        cell: ({ row }) => formatDate(row.original.uploadedAt),
        meta: { filterType: "date" },
    },
    {
        accessorKey: "originalFilename",
        header: "File Name",
        cell: ({ row }) => (
            <div className="max-w-xs truncate font-medium" title={row.original.originalFilename}>
                {row.original.originalFilename}
            </div>
        ),
        meta: { filterType: "text", filterPlaceholder: "Search file name..." },
    },
];

export const FilesTable = memo(function FilesTable({
    files,
    loading,
    onFileClick,
    className,
    columnFilters,
    initialColumnFilters,
    onColumnFiltersChange,
    sortingState,
    onSortingChange,
    rowCount,
    businessPartnerOptions = [],
    submissionOptions = [],
    enableFilters = true,
    hiddenColumns,
    availableFileTypes,
    showArchiveColumns = false,
    storageKey,
    emptyMessage = "No files found. Upload a file to get started.",
    showRowCount = true,
}: TFilesTableProps) {
    // Only enable manual (server-driven) mode when this table is actually wired
    // up to server-side handlers. When enableFilters is false (e.g. the Dashboard
    // widget) or no handlers are provided, the table runs in fully client-side
    // mode so no dead filter controls appear.
    const serverDriven = enableFilters && (!!onColumnFiltersChange || !!onSortingChange);
    const columns = useMemo(() => {
        const fileTypeOptions = availableFileTypes
            ? ALL_FILE_TYPE_OPTIONS.filter((o) => availableFileTypes.has(o.value))
            : ALL_FILE_TYPE_OPTIONS;
        const all = [
            ...createColumns({ businessPartnerOptions, submissionOptions, fileTypeOptions }),
            // No `meta.filterType`: /files/filter-options has no archive facets, so a
            // filter control here would be dead. Sorting works — the server knows
            // both keys.
            ...(showArchiveColumns ? archiveColumns : []),
        ];
        return hiddenColumns?.length
            ? all.filter(
                  (col) =>
                      !hiddenColumns.includes((col as { accessorKey?: string }).accessorKey ?? ""),
              )
            : all;
    }, [
        hiddenColumns,
        availableFileTypes,
        businessPartnerOptions,
        submissionOptions,
        showArchiveColumns,
    ]);

    return (
        <div className={cn(className)}>
            <DataTable
                columns={columns}
                data={files}
                loading={loading}
                onRowClick={onFileClick}
                emptyMessage={emptyMessage}
                initialColumnFilters={columnFilters ?? initialColumnFilters}
                onColumnFiltersChange={onColumnFiltersChange}
                sortingState={sortingState}
                onSortingChange={onSortingChange}
                manualFiltering={serverDriven}
                manualSorting={serverDriven}
                rowCount={rowCount}
                rowNoun={{ one: "file", many: "files" }}
                enableFiltering={enableFilters}
                showRowCount={showRowCount}
                storageKey={storageKey}
            />
        </div>
    );
});
